import React from "react";
import {
  FORMAT_TEXT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  $getSelection,
  $getRoot,
  $createTextNode,
  $isRangeSelection,
  $isTextNode,
  $isElementNode,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  FontSizeOutlined,
  BoldOutlined,
  ItalicOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TableOutlined,
  LineHeightOutlined,
  FontColorsOutlined,
  BgColorsOutlined,
  FileImageOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import "../../styles/EditorTheme.css";
import ColorPicker from "../ColoPicker/ColorPicker";
import BlockFormatMenu from "../BlockFormatMenu/BlockFormatMenu";
import useEditorToolbar from "../../hooks/useEditorToolbar";
import useColorPicker from "../../hooks/useColorPicker";
import { ToolbarProps } from "../../types";
import { Button, Dropdown, Flex } from "antd";
import AlignMenu from "../AlignMenu/AlignMenu";
import useTextEditor from "../../hooks/useTextEditor";
import TextEditor from "../TextEditor/TextEditor";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import InsertLink from "../InsertLink/InsertLink";
import { INSERT_IMAGE_COMMAND } from "../Image/Image";
import { INSERT_TABLE_COMMAND } from "@lexical/table";
import Table from "../table/Table";
import { Upload, message } from "antd";
import { $createBeautifulMentionNode } from "lexical-beautiful-mentions";
import { useTranslation } from "react-i18next";

const root: React.CSSProperties = {
  width: "100%",
  height: "48px",
  backgroundColor: "white",
  padding: "0px 16px",
  borderTopLeftRadius: "5px",
  borderTopRightRadius: "5px",
  border: "1px solid #d9d9d9",
  borderBottom: "0px",
  margin: "0px",
  boxSizing: "border-box",
};

const icon: React.CSSProperties = {
  color: "black",
  cursor: "pointer",
  fontSize: "16px",
};

const Toolbar: React.FC<ToolbarProps> = ({ editable, widgetItems }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation();

  const { blockType } = useEditorToolbar();

  const handleLinkClick = (url: string) => {
    // Perform action with the URL, for example, dispatching a command
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  };

  const handleTableClick = (columns: string, rows: string) => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: columns,
      rows: rows,
    });
  };

  const customRequest = ({ file, onSuccess }: any) => {
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
      altText: file.name,
      src: URL.createObjectURL(file),
    });
    onSuccess(); // Triggered after the image is successfully uploaded
  };

  const onImageUpload = (info: any) => {
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed`);
    }
  };

  const handleDropdownSelect = (item: any) => {
    editor.focus();
    setTimeout(() => {
      editor.update(() => {
        let selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          const lastDescendant = $getRoot().getLastDescendant();
          if (lastDescendant) {
            lastDescendant.selectEnd();
            selection = $getSelection();
          }
        }
        if (!$isRangeSelection(selection)) return;

        const anchorNode = selection.anchor.getNode();
        const mentionNode = $createBeautifulMentionNode("@", item.label, { id: item.id });
        const spaceNode = $createTextNode(" ");

        if ($isTextNode(anchorNode)) {
          const offset = selection.anchor.offset;
          if (offset === anchorNode.getTextContentSize()) {
            anchorNode.insertAfter(mentionNode);
          } else {
            const [, afterNode] = anchorNode.splitText(offset);
            if (afterNode) {
              afterNode.insertBefore(mentionNode);
            } else {
              anchorNode.insertAfter(mentionNode);
            }
          }
          mentionNode.insertAfter(spaceNode);
        } else if ($isElementNode(anchorNode)) {
          anchorNode.append(mentionNode, spaceNode);
        }
        spaceNode.select();
      });
    }, 0);
  };

  const { onFontColorSelect, onBgColorSelect } = useColorPicker();
  const { onFontSizeSelect, onFontLineSelect } = useTextEditor();

  if (!editable) return null;

  return (
    <Flex gap={16} style={root} align="center">
      <TextEditor
        title={t('Font-Size')}
        icon={<FontSizeOutlined style={icon} />}
        onClick={(v) => {
          onFontSizeSelect(v + "px")
        }}
      />

      <BoldOutlined
        style={icon}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}
      />

      <ItalicOutlined
        style={icon}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}
      />

      <BlockFormatMenu blockType={blockType} />

      <MenuFoldOutlined
        style={icon}
        onClick={() =>
          editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)
        }
      />

      <MenuUnfoldOutlined
        style={icon}
        onClick={() =>
          editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)
        }
      />

      <AlignMenu />

      <Table icon={<TableOutlined style={icon} />} onClick={handleTableClick} />

      <InsertLink onClick={handleLinkClick} style={icon} />

      <TextEditor
        title={t('Line-Height')}
        icon={<LineHeightOutlined style={icon} />}
        onClick={onFontLineSelect}
      />

      <ColorPicker
        key="color-picker"
        title={t('Font color')}
        onChange={(color) => onFontColorSelect(color)}
        icon={<FontColorsOutlined style={icon} />}
      />

      <ColorPicker
        key="bg-color-picker"
        title={t('Background color')}
        onChange={(color) => onBgColorSelect(color)}
        icon={<BgColorsOutlined style={icon} />}
      />

      <Upload
        accept="image/*"
        showUploadList={false}
        customRequest={customRequest}
        onChange={onImageUpload}
      >
        <FileImageOutlined style={icon} />
      </Upload>
      {/* <Dropdown menu={{ items: widgetItems }} >
        <Button style={{ color: "#141BCB", borderColor: "#141BCB" }}><span style={{ paddingRight: '3px' }} ><PlusOutlined /></span>Form Field</Button>
      </Dropdown> */}
      {widgetItems && widgetItems.length > 0 && (
        <Dropdown
          menu={{
            items: widgetItems.map((item: any) => ({
              // Already resolved for the selected template language — do not
              // re-translate into the app locale.
              label: item.label,
              key: item.key || item.id,
              type: "item",
              onClick: () => handleDropdownSelect(item),
            })),
          }}
          trigger={['click']}
        >
          <Button style={{ color: "#141BCB", borderColor: "#141BCB" }}>
            <span style={{ paddingRight: "3px" }}>
              <PlusOutlined />
            </span>
            {t("Form Field")}
          </Button>
        </Dropdown>
      )
      }
    </Flex>
  );
};

export default Toolbar;
